// ═══════════════════════════════════════════════════════════════════════════
// 🗺️ SEMANTIC MAP VIEW - Visualizzazione interattiva del Knowledge Graph
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';

/**
 * Vista Mappa Semantica - Knowledge Graph interattivo con processo RAG
 *
 * @param {Array} categories - Array di categorie con items
 * @param {Array} memories - Array di tutti i memories
 */
function SemanticMapView({ categories = [], memories = [] }) {
  const [selectedNode, setSelectedNode] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [questionData, setQuestionData] = useState({
    question: '',
    keywords: [],
    category: '',
    memories: [],
    response: ''
  });

  // Dati di esempio per il grafo (default)
  const defaultNodesData = {
    famiglia: {
      title: 'Famiglia',
      desc: 'Ricordi dei familiari',
      examples: ['Figlia Marco', 'Nipote Luca', 'Marito Giuseppe'],
      keyword: 'figlia',
      response: 'Marco, la tua adorata figlia, vive qui vicino e viene a trovarti spesso. Ti vuole tanto bene!'
    },
    animali: {
      title: 'Animali',
      desc: 'Animali domestici',
      examples: ['Cane Fido', 'Gatto Micio'],
      keyword: 'cane',
      response: 'Il tuo cagnolino si chiama Fido! È dolcissimo e ti fa sempre le feste.'
    },
    casa: {
      title: 'Casa',
      desc: 'Luoghi della casa',
      examples: ['Cucina', 'Giardino', 'Camera'],
      keyword: 'abito',
      response: 'Questa è casa tua! Sei nella tua bella casa con il giardino che ami tanto.'
    },
    viaggi: {
      title: 'Viaggi',
      desc: 'Vacanze e gite',
      examples: ['Rimini', 'Roma', 'Lago'],
      keyword: 'viaggio',
      response: 'Ti piaceva molto viaggiare! Ricordi la vacanza a Rimini?'
    },
    hobby: {
      title: 'Hobby',
      desc: 'Passioni',
      examples: ['Cucito', 'Giardinaggio'],
      keyword: 'hobby',
      response: 'Ti piace il cucito e curare il giardino!'
    },
    lavoro: {
      title: 'Lavoro',
      desc: 'Vita professionale',
      examples: ['Maestra', '30 anni di servizio'],
      keyword: 'lavoro',
      response: 'Hai fatto la maestra elementare per 30 anni! I tuoi alunni ti adoravano.'
    },
    musica: {
      title: 'Musica',
      desc: 'Canzoni amate',
      examples: ['Mina', 'Claudio Villa'],
      keyword: 'canzone',
      response: 'Ti piacciono le canzoni degli anni 60! Mina è la tua preferita.'
    },
    cibo: {
      title: 'Cibo',
      desc: 'Piatti preferiti',
      examples: ['Lasagne', 'Tiramisù'],
      keyword: 'cucino',
      response: 'Ti piace cucinare le lasagne della domenica!'
    }
  };

  // Sovrascrive i dati di esempio con dati reali se disponibili
  const nodesData = { ...defaultNodesData };

  if (categories && categories.length > 0) {
    categories.forEach(cat => {
      // Usa il nome della categoria in lowercase come ID nodo
      const catLower = cat.name.toLowerCase().trim();
      const items = cat.items || [];

      // Normalizza i nomi delle categorie per matchare i nodi esistenti
      const normalizedId = catLower.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

      // Se il nodo esiste già, aggiorna con dati reali
      if (nodesData[normalizedId] || nodesData[catLower]) {
        const nodeId = nodesData[normalizedId] ? normalizedId : catLower;
        nodesData[nodeId] = {
          title: cat.name,
          desc: `${cat.count || items.length} ricord${(cat.count || items.length) === 1 ? 'o' : 'i'}`,
          examples: items.slice(0, 3).map(item => item.title || item.description || 'Senza titolo').filter(Boolean),
          keyword: cat.name.toLowerCase(),
          response: items.length > 0
            ? `Ecco i ricordi in ${cat.name}: ${items.slice(0, 2).map(i => i.title).join(', ')}${items.length > 2 ? ' e altri...' : ''}`
            : `Non ci sono ancora ricordi in ${cat.name}`
        };
      }
    });
  }

  const questionMap = {
    'figlia': 'famiglia',
    'cane': 'animali',
    'abito': 'casa',
    'lavoro': 'lavoro',
    'canzone': 'musica',
    'cucino': 'cibo'
  };

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  const findCategory = (q) => {
    const lower = q.toLowerCase();
    for (const [key, cat] of Object.entries(questionMap)) {
      if (lower.includes(key)) return cat;
    }
    return 'famiglia';
  };

  const handleNodeClick = (nodeId) => {
    if (isAnimating) return;
    setSelectedNode(nodeId);
  };

  const simulateRAG = async (question) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setSelectedNode(null);
    setActiveStep(0);
    setQuestionData({
      question: '',
      keywords: [],
      category: '',
      memories: [],
      response: ''
    });

    const catId = findCategory(question);
    const data = nodesData[catId];

    // Step 1: Domanda
    await sleep(200);
    setActiveStep(1);
    setQuestionData(prev => ({ ...prev, question }));
    await sleep(700);

    // Step 2: Analisi Semantica
    setActiveStep(2);
    setQuestionData(prev => ({ ...prev, keywords: [data.keyword] }));
    await sleep(700);

    // Step 3: Ricerca nel Grafo
    setActiveStep(3);
    setQuestionData(prev => ({ ...prev, category: data.title }));
    setSelectedNode(catId);
    await sleep(1200);

    // Step 4: Recupero Ricordi
    setActiveStep(4);
    for (let i = 0; i < data.examples.length; i++) {
      await sleep(250);
      setQuestionData(prev => ({
        ...prev,
        memories: [...prev.memories, data.examples[i]]
      }));
    }
    await sleep(600);

    // Step 5: Risposta
    setActiveStep(5);
    setQuestionData(prev => ({ ...prev, response: data.response }));

    await sleep(2000);
    setIsAnimating(false);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Come funziona l'AI Semantica</h1>
        <p style={styles.subtitle}>Clicca su una domanda per vedere il processo</p>
      </div>

      <div style={styles.mainLayout}>
        {/* GRAFO */}
        <div style={styles.graphSection}>
          <div style={styles.sectionLabel}>Knowledge Graph</div>

          <div style={styles.graphContainer}>
            <svg width="340" height="340" viewBox="0 0 340 340">
              <defs>
                <linearGradient id="nodeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#f0fdf4"/>
                  <stop offset="100%" stopColor="#dcfce7"/>
                </linearGradient>
                <linearGradient id="centerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#22c55e"/>
                  <stop offset="100%" stopColor="#16a34a"/>
                </linearGradient>
                <filter id="softShadow">
                  <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.08"/>
                </filter>
              </defs>

              {/* Connessioni */}
              <g>
                <line className={selectedNode === 'famiglia' && activeStep >= 3 ? 'connection-active' : 'connection'} x1="170" y1="170" x2="170" y2="50" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4,4"/>
                <line className={selectedNode === 'animali' && activeStep >= 3 ? 'connection-active' : 'connection'} x1="170" y1="170" x2="270" y2="85" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4,4"/>
                <line className={selectedNode === 'casa' && activeStep >= 3 ? 'connection-active' : 'connection'} x1="170" y1="170" x2="295" y2="170" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4,4"/>
                <line className={selectedNode === 'viaggi' && activeStep >= 3 ? 'connection-active' : 'connection'} x1="170" y1="170" x2="270" y2="255" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4,4"/>
                <line className={selectedNode === 'hobby' && activeStep >= 3 ? 'connection-active' : 'connection'} x1="170" y1="170" x2="170" y2="290" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4,4"/>
                <line className={selectedNode === 'lavoro' && activeStep >= 3 ? 'connection-active' : 'connection'} x1="170" y1="170" x2="70" y2="255" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4,4"/>
                <line className={selectedNode === 'musica' && activeStep >= 3 ? 'connection-active' : 'connection'} x1="170" y1="170" x2="45" y2="170" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4,4"/>
                <line className={selectedNode === 'cibo' && activeStep >= 3 ? 'connection-active' : 'connection'} x1="170" y1="170" x2="70" y2="85" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4,4"/>
              </g>

              {/* Nodo centrale AI */}
              <g>
                <circle cx="170" cy="170" r="38" fill="url(#centerGradient)" filter="url(#softShadow)"/>
                <text x="170" y="176" textAnchor="middle" fontSize="14" fill="white" fontWeight="600">AI</text>
              </g>

              {/* Nodi categoria */}
              {Object.entries(nodesData).map(([id, data], idx) => {
                const positions = {
                  famiglia: { cx: 170, cy: 50 },
                  animali: { cx: 270, cy: 85 },
                  casa: { cx: 295, cy: 170 },
                  viaggi: { cx: 270, cy: 255 },
                  hobby: { cx: 170, cy: 290 },
                  lavoro: { cx: 70, cy: 255 },
                  musica: { cx: 45, cy: 170 },
                  cibo: { cx: 70, cy: 85 }
                };
                const pos = positions[id];
                const isPulsing = selectedNode === id && activeStep >= 3 && activeStep < 5;

                return (
                  <g key={id} onClick={() => handleNodeClick(id)} style={{ cursor: 'pointer' }} className={isPulsing ? 'node-pulsing' : ''}>
                    <circle cx={pos.cx} cy={pos.cy} r="30" fill="url(#nodeGradient)" stroke="#22c55e" strokeWidth="2" filter="url(#softShadow)"/>
                    <text x={pos.cx} y={pos.cy + 5} textAnchor="middle" fontSize="11" fill="#374151">{data.title}</text>
                  </g>
                );
              })}
            </svg>

            <style>{`
              .connection-active {
                stroke: #22c55e !important;
                stroke-opacity: 0.6;
                stroke-width: 2.5;
                stroke-dasharray: none !important;
              }
              .node-pulsing circle {
                animation: gentlePulse 1.2s ease-in-out infinite;
              }
              @keyframes gentlePulse {
                0%, 100% {
                  filter: drop-shadow(0 0 0 rgba(34, 197, 94, 0));
                  transform: scale(1);
                }
                50% {
                  filter: drop-shadow(0 0 20px rgba(34, 197, 94, 0.4));
                  transform: scale(1.08);
                }
              }
            `}</style>
          </div>

          {/* Info nodo */}
          {selectedNode && !isAnimating && (
            <div style={styles.nodeInfo}>
              <h4 style={styles.nodeInfoTitle}>{nodesData[selectedNode].title}</h4>
              <p style={styles.nodeInfoDesc}>{nodesData[selectedNode].desc}</p>
              <div style={styles.tagContainer}>
                {nodesData[selectedNode].examples.map((ex, i) => (
                  <span key={i} style={styles.tag}>{ex}</span>
                ))}
              </div>
            </div>
          )}

          {/* Legenda */}
          <div style={styles.legend}>
            <div style={styles.legendItem}>
              <svg width="20" height="8">
                <line x1="0" y1="4" x2="20" y2="4" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4,4"/>
              </svg>
              <span>Connessione</span>
            </div>
            <div style={styles.legendItem}>
              <div style={styles.legendDot}></div>
              <span>Categoria</span>
            </div>
          </div>
        </div>

        {/* PROCESSO RAG */}
        <div style={styles.processSection}>
          <div style={styles.sectionLabel}>Processo RAG</div>

          <div style={styles.stepsContainer}>
            {/* Step 1 */}
            <div style={{...styles.step, ...(activeStep === 1 ? styles.stepActive : activeStep > 1 ? styles.stepDone : {})}}>
              <div style={{...styles.stepNumber, ...(activeStep >= 1 ? styles.stepNumberActive : {})}}>1</div>
              <div style={styles.stepContent}>
                <h4 style={styles.stepTitle}>Domanda</h4>
                <p style={styles.stepDesc}>La persona fa una domanda all'AI</p>
                {questionData.question && (
                  <div style={styles.tagContainer}>
                    <span style={styles.tag}>"{questionData.question}"</span>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2 */}
            <div style={{...styles.step, ...(activeStep === 2 ? styles.stepActive : activeStep > 2 ? styles.stepDone : {})}}>
              <div style={{...styles.stepNumber, ...(activeStep >= 2 ? styles.stepNumberActive : {})}}>2</div>
              <div style={styles.stepContent}>
                <h4 style={styles.stepTitle}>Analisi Semantica</h4>
                <p style={styles.stepDesc}>L'AI trova le parole chiave</p>
                {questionData.keywords.length > 0 && (
                  <div style={styles.tagContainer}>
                    {questionData.keywords.map((kw, i) => (
                      <span key={i} style={styles.tag}>{kw}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Step 3 */}
            <div style={{...styles.step, ...(activeStep === 3 ? styles.stepActive : activeStep > 3 ? styles.stepDone : {})}}>
              <div style={{...styles.stepNumber, ...(activeStep >= 3 ? styles.stepNumberActive : {})}}>3</div>
              <div style={styles.stepContent}>
                <h4 style={styles.stepTitle}>Ricerca nel Grafo</h4>
                <p style={styles.stepDesc}>Cerca la categoria corrispondente</p>
                {questionData.category && (
                  <div style={styles.tagContainer}>
                    <span style={styles.tag}>{questionData.category}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Step 4 */}
            <div style={{...styles.step, ...(activeStep === 4 ? styles.stepActive : activeStep > 4 ? styles.stepDone : {})}}>
              <div style={{...styles.stepNumber, ...(activeStep >= 4 ? styles.stepNumberActive : {})}}>4</div>
              <div style={styles.stepContent}>
                <h4 style={styles.stepTitle}>Recupero Ricordi</h4>
                <p style={styles.stepDesc}>Estrae i ricordi dalla categoria</p>
                {questionData.memories.length > 0 && (
                  <div style={styles.tagContainer}>
                    {questionData.memories.map((mem, i) => (
                      <span key={i} style={styles.tag}>{mem}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Step 5 */}
            <div style={{...styles.step, ...(activeStep === 5 ? styles.stepActive : activeStep > 5 ? styles.stepDone : {})}}>
              <div style={{...styles.stepNumber, ...(activeStep >= 5 ? styles.stepNumberActive : {})}}>5</div>
              <div style={styles.stepContent}>
                <h4 style={styles.stepTitle}>Risposta Empatica</h4>
                <p style={styles.stepDesc}>Genera una risposta personalizzata</p>
                {questionData.response && (
                  <div style={styles.aiResponse}>{questionData.response}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* DOMANDE */}
        <div style={styles.questionsSection}>
          <div style={styles.questionsTitle}>Prova una domanda</div>
          <div style={styles.questionsGrid}>
            <button style={styles.questionBtn} onClick={() => simulateRAG('Dov\'è mia figlia?')} disabled={isAnimating}>
              Dov'è mia figlia?
            </button>
            <button style={styles.questionBtn} onClick={() => simulateRAG('Come si chiama il cane?')} disabled={isAnimating}>
              Come si chiama il cane?
            </button>
            <button style={styles.questionBtn} onClick={() => simulateRAG('Dove abito?')} disabled={isAnimating}>
              Dove abito?
            </button>
            <button style={styles.questionBtn} onClick={() => simulateRAG('Che lavoro facevo?')} disabled={isAnimating}>
              Che lavoro facevo?
            </button>
            <button style={styles.questionBtn} onClick={() => simulateRAG('La mia canzone?')} disabled={isAnimating}>
              La mia canzone?
            </button>
            <button style={styles.questionBtn} onClick={() => simulateRAG('Cosa cucino?')} disabled={isAnimating}>
              Cosa cucino?
            </button>
          </div>
        </div>
      </div>

      {/* Nota */}
      <div style={styles.infoNote}>
        Ogni nodo rappresenta una categoria di ricordi. Le connessioni mostrano le relazioni semantiche.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════

const styles = {
  container: {
    padding: '0',
  },

  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },

  title: {
    fontSize: '22px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '8px',
    letterSpacing: '-0.3px',
  },

  subtitle: {
    fontSize: '15px',
    color: '#666',
  },

  mainLayout: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '32px',
  },

  graphSection: {
    width: '100%',
    maxWidth: '400px',
  },

  sectionLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#22c55e',
    textAlign: 'center',
    marginBottom: '16px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  graphContainer: {
    display: 'flex',
    justifyContent: 'center',
  },

  nodeInfo: {
    marginTop: '16px',
    padding: '16px',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #fafafa 100%)',
    borderRadius: '12px',
    border: '1px solid #d1fae5',
    textAlign: 'center',
  },

  nodeInfoTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#166534',
    marginBottom: '6px',
  },

  nodeInfoDesc: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '10px',
  },

  legend: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginTop: '16px',
    fontSize: '12px',
    color: '#9ca3af',
  },

  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  legendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
    border: '1.5px solid #22c55e',
  },

  processSection: {
    width: '100%',
    maxWidth: '500px',
  },

  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },

  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px 20px',
    background: '#fafafa',
    borderRadius: '14px',
    border: '1.5px solid transparent',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: 0.4,
  },

  stepActive: {
    opacity: 1,
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderColor: '#86efac',
    boxShadow: '0 4px 20px rgba(34, 197, 94, 0.15)',
    transform: 'translateX(4px)',
  },

  stepDone: {
    opacity: 0.7,
    background: '#f8faf9',
    borderColor: '#d1fae5',
  },

  stepNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#e5e7eb',
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    flexShrink: 0,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  stepNumberActive: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: 'white',
    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
  },

  stepContent: {
    flex: 1,
  },

  stepTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '4px',
    letterSpacing: '-0.2px',
  },

  stepDesc: {
    fontSize: '13px',
    color: '#666',
    margin: 0,
  },

  tagContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '10px',
  },

  tag: {
    background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
    color: '#166534',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: 500,
    boxShadow: '0 1px 3px rgba(34, 197, 94, 0.1)',
  },

  aiResponse: {
    marginTop: '12px',
    padding: '16px',
    background: 'linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)',
    borderRadius: '12px',
    fontSize: '14px',
    color: '#166534',
    lineHeight: '1.6',
    borderLeft: '3px solid #22c55e',
  },

  questionsSection: {
    width: '100%',
    maxWidth: '500px',
    marginTop: '24px',
    padding: '24px',
    background: '#fafafa',
    borderRadius: '16px',
  },

  questionsTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '16px',
    textAlign: 'center',
  },

  questionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
  },

  questionBtn: {
    padding: '14px 16px',
    background: '#fff',
    border: '1.5px solid #e5e7eb',
    borderRadius: '12px',
    color: '#374151',
    fontSize: '14px',
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    textAlign: 'left',
  },

  infoNote: {
    marginTop: '24px',
    padding: '14px 18px',
    background: '#f8f9fa',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#666',
    textAlign: 'center',
    borderLeft: '3px solid #22c55e',
  },
};

export default SemanticMapView;
